import AppCommandBar from 'app/AppCommandBar'
import { AutoStack, FlexSpinner } from 'components'
import { DataStorageLabelSwitcher } from 'components/DataStorageLabelSwitcher'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { RichTextViewer } from 'components/RichTextViewer/RichTextViewer'
import { FormikActions } from 'formik'
import {
  ProvingDetailsDocumentForm,
  ProvingDetailsDocumentFormSchema,
} from 'forms/ProvingDetailsForm'
import { ProvingDetailsAnnotationForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsAnnotationForm'
import { canUpdateProving, isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCommand } from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import useSelection from 'hooks/useSelection'
import _, { startsWith } from 'lodash'
import { IconButton } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDeleteDialog } from '../../dialogs/ConfirmDeleteDialog'
import {
  Blob,
  Participant,
  ProvingDocument,
  ProvingUserRole,
  UserRole,
} from '../../model'
import { DataStoragePagination } from './DataStoragePagination'
import {
  useAddProvingDocumentsMutation,
  useDeleteProvingDocumentsMutation,
  useLoadProvingDocumentsQuery,
  useUpdateProvingDocumentsMutation,
  useUpdateProvingMutation,
} from './hooks'
import { Page, Section } from './Layout'
import { DocumentList } from './lists/DocumentList'
import { FerryApprovalList } from './lists/FerryApprovalList'
import {
  FlightApprovalList,
  HotelApprovalList,
  RentalCarApprovalList,
} from './lists/index'
import { RouteHotelApprovalList } from './lists/RouteHotelApprovalList'

enum FormType {
  annotation,
}

export const PageDocuments: React.FC = () => {
  const { t } = useTranslation()
  const [proving, refetch] = useCurrentProving()
  const [updateProving] = useUpdateProvingMutation()
  const [formType, setFormType] = React.useState<FormType>()
  let canUpdateCurrentProving = canUpdateProving(proving)
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isHrg = auth.hasUserRole(UserRole.Hrg)
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  if (isServiceTeam === true) {
    canUpdateCurrentProving = isServiceTeam
  }
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const canUploadFiles = () => {
    return canUpdateCurrentProving || isHrg
  }

  const canUseCommand = canUpdateCurrentProving && !isMaintenanceHead
  const [dialogState, showDialog, hideDialog] = useDialogState()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [annotationPanelState, showAnnotationPanel, hideAnnotationPanel] =
    useFormikPanelState()
  const provingDocuments = useLoadProvingDocumentsQuery({
    variables: { id: proving.id },
  })
  const [params, updateParams] = useRouteParams('provings.details', {
    page: 'files',
    block: 'default',
  })

  const handleCategorySelected = React.useCallback(
    (block: string) => updateParams({ block }),
    [updateParams]
  )

  const [selection, selectedItems] = useSelection<ProvingDocument>({
    getKey: (item) => item._id!,
  })
  const [addDocuments] = useAddProvingDocumentsMutation({
    onCompleted: (d) => {
      provingDocuments.refetch()
    },
  })

  const [updateDocuments] = useUpdateProvingDocumentsMutation({
    onCompleted: (d) => {
      provingDocuments.refetch()
    },
  })

  const [deleteDocuments] = useDeleteProvingDocumentsMutation({
    onCompleted: (d) => {
      provingDocuments.refetch()
    },
  })

  useCommand(
    {
      key: 'editDocument',
      text: t('model.proving.documents.edit'),
      iconProps: { iconName: 'Edit' },
      headerText: 'header',
      hidden: !!isProvingManager,
      disabled:
        proving.status === 8 ||
        proving.status === 9 ||
        selectedItems.length === 0 ||
        selectedItems[0].type === 'Generelle Information' ||
        !!isProvingManager ||
        !!isMaintenanceHead,
      onClick: () => {
        if (selectedItems.length === 1) {
          showPanel({
            participantId: selectedItems[0]._id,
            ...selectedItems[0],
          })
        } else {
          showPanel({})
        }

        if (panelState.isOpen) {
          hidePanel()
        }
      },
    },
    [selectedItems]
  )

  useCommand(
    {
      key: 'deleteDocument',
      text: t('model.proving.documents.delete'),
      iconProps: { iconName: 'Delete' },
      headerText: 'Delete',
      disabled:
        proving.status === 8 ||
        proving.status === 9 ||
        selectedItems.length === 0 ||
        selectedItems[0].type === 'Generelle Information' ||
        _.startsWith(params.block, 'fix') ||
        !!isProvingManager ||
        !!isMaintenanceHead,
      hidden: _.startsWith(params.block, 'fix') || !!isProvingManager,
      onClick: () => {
        showDialog()
      },
    },
    [selectedItems]
  )

  const documents = _.get(
    provingDocuments.data,
    'proving.documents',
    []
  ) as ProvingDocument[]
  const participants = _.get(
    provingDocuments.data,
    'proving.participants',
    []
  ) as Participant[]

  const defaultItems = () => {
    const block: any[] = []
    documents.forEach((doc) => {
      if ((doc.block === 'default' || doc.block === undefined) && !doc.type) {
        block.push(doc)
      }
    })
    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    return provDocs
  }
  // const getFlightApprovalItems = () => {
  //   return documents.map(x => {
  //     const participant = _.find(
  //       participants,
  //       y =>
  //         y._id === x.participantId &&
  //         x.type === ProvingDocumentType.BookingConfirmationFlight
  //     )
  //     return {
  //       ...x,
  //       participant,
  //     }
  //   })
  // }
  const block1Items = () => {
    const pgDocs =
      proving &&
      proving.provingGround &&
      proving.provingGround.block1Files &&
      proving.provingGround.block1Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
          participantId: '',
        }
      })

    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block1)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })

    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }

  const block2Items = () => {
    const pgDocs =
      proving &&
      proving.provingGround &&
      proving.provingGround.block2Files &&
      proving.provingGround.block2Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block2)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }
  const block3Items = () => {
    const pgDocs =
      proving &&
      proving.provingGround &&
      proving.provingGround.block3Files &&
      proving.provingGround.block3Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block3)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }
  const block4Items = () => {
    const pgDocs =
      proving.provingGround &&
      proving.provingGround.block4Files &&
      proving.provingGround.block4Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block4)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }
  const block5Items = () => {
    const pgDocs =
      proving.provingGround &&
      proving.provingGround.block5Files &&
      proving.provingGround.block5Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block5)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }
  const block6Items = () => {
    const pgDocs =
      proving.provingGround &&
      proving.provingGround.block6Files &&
      proving.provingGround.block6Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block6)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }
  const block7Items = () => {
    const pgDocs =
      proving.provingGround &&
      proving.provingGround.block7Files &&
      proving.provingGround.block7Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block7)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }
  const block8Items = () => {
    const pgDocs =
      proving.provingGround &&
      proving.provingGround.block8Files &&
      proving.provingGround.block8Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block8)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }
  const block9Items = () => {
    const pgDocs =
      proving.provingGround &&
      proving.provingGround.block9Files &&
      proving.provingGround.block9Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block9)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }
  const block10Items = () => {
    const pgDocs =
      proving.provingGround &&
      proving.provingGround.block10Files &&
      proving.provingGround.block10Files.map((bf) => {
        const stringToReplace = bf.split('/').pop()
        return {
          uri: bf,
          name: stringToReplace ? stringToReplace.replace(/%20/g, ' ') : '',
          type: 'Generelle Information',
        }
      })
    const block: any[] = []
    documents.forEach((doc) => {
      if (
        doc.type ===
        (proving && proving.provingGround && proving.provingGround.block10)
      ) {
        block.push(doc)
      }
    })

    const provDocs: any[] = block.map((x) => {
      const participant = _.find(participants, (y) => y._id === x.participantId)
      return {
        ...x,
        participant,
      }
    })
    const blockDocs = pgDocs ? pgDocs.concat(provDocs) : provDocs
    return blockDocs
  }

  const handleFileUpload = async (files: Partial<Blob>[]) => {
    let type: any
    if (params.block) {
      type =
        params.block === 'block1'
          ? proving.provingGround!.block1
          : params.block === 'block2'
          ? proving.provingGround!.block2
          : params.block === 'block3'
          ? proving.provingGround!.block3
          : params.block === 'block4'
          ? proving.provingGround!.block4
          : params.block === 'block5'
          ? proving.provingGround!.block5
          : params.block === 'block6'
          ? proving.provingGround!.block6
          : params.block === 'block7'
          ? proving.provingGround!.block7
          : params.block === 'block8'
          ? proving.provingGround!.block8
          : params.block === 'block9'
          ? proving.provingGround!.block9
          : params.block === 'block10'
          ? proving.provingGround!.block10
          : params.block === 'fix1'
          ? ''
          : params.block === 'fix2'
          ? ''
          : params.block === 'fix3'
          ? ''
          : params.block === 'fix4'
          ? ''
          : params.block === 'fix5'
          ? ''
          : ''
    }
    const input = files.map((f) => {
      return {
        uri: f.uri!,
        name: f.name || '',
        type,
        block: startsWith(params.block, 'fix') ? 'default' : params.block,
      }
    })
    await addDocuments({
      variables: {
        provingId: proving._id!,
        input,
      },
    })
  }

  const handleSubmit = async (values: any, actions: FormikActions<any>) => {
    actions.setSubmitting(true)
    try {
      let block: string

      switch (values.type) {
        case 'bookingConfirmationRouteHotel':
          block = 'fix5'
          break
        case 'bookingConfirmationFerry':
          block = 'fix4'
          break
        case 'bookingConfirmationHotel':
          block = 'fix1'
          break
        case 'bookingConfirmationRentalCar':
          block = 'fix2'
          break
        case 'bookingConfirmationFlight':
          block = 'fix3'
          break
        default:
          block = values.block ? values.block : 'default'
      }

      await updateDocuments({
        variables: {
          provingId: proving._id,
          input: selectedItems.map((x) => {
            return {
              documentId: x._id,
              participantId: values.participantId,
              type: values.type,
              block,
            }
          }),
        },
      })
      actions.setStatus(null)
    } catch (err) {
      actions.setStatus({ error: err })
    } finally {
      actions.setSubmitting(false)
    }
    hidePanel()
  }

  const handleAnnotationSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const { errors } = await updateProving({
        variables: {
          id: proving.id,
          update: {
            annotationFiles: values.annotation,
          },
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      refetch()
      hideAnnotationPanel()
    },
    [proving, updateProving, hideAnnotationPanel, refetch]
  )

  return (
    <FlexSpinner
      loading={provingDocuments.loading}
      error={provingDocuments.error}
    >
      <Page>
        <Section label={t('section.files.annotation')}>
          <AutoStack horizontal={true}>
            <IconButton
              iconProps={{ iconName: 'Edit' }}
              disabled={!canUseCommand || !!isProvingManager}
              onClick={() => {
                setFormType(FormType.annotation)
                showAnnotationPanel({
                  annotation: proving.annotationFiles || '',
                })
              }}
            />

            <RichTextViewer
              richText={proving.annotationFiles ? proving.annotationFiles : ''}
            />
          </AutoStack>
        </Section>
        <DataStoragePagination
          selectedCategory={params.block}
          onSelectCategory={handleCategorySelected}
          proving={proving}
          provingGround={proving.provingGround}
        />
        <Section label={t('model.proving.documents.upload')}>
          <FileUpload
            parentId={proving._id!}
            parentType={proving.__typename!}
            onUploadCompleted={handleFileUpload}
            disabled={!canUploadFiles() || proving.status === 8}
          />
        </Section>
        <DataStorageLabelSwitcher params={params.block} proving={proving} />
        <Section label={t('section.files.header')}>
          {!_.startsWith(params.block, 'fix') && <AppCommandBar />}
          {params.block === 'default' && (
            <DocumentList items={defaultItems()} selection={selection} />
          )}
          {params.block === 'fix3' && (
            <FlightApprovalList
              items={
                proving && proving.participants ? proving!.participants : []
              }
              provingGround={proving.provingGround}
              selection={selection}
              proving={proving}
              params={params}
              participants={participants}
            />
          )}
          {params.block === 'fix1' && (
            <HotelApprovalList
              items={
                proving && proving.participants ? proving!.participants : []
              }
              provingGround={proving.provingGround}
              selection={selection}
              proving={proving}
              params={params}
              participants={participants}
            />
          )}
          {params.block === 'fix2' && (
            <RentalCarApprovalList
              items={
                proving && proving.participants ? proving!.participants : []
              }
              provingGround={proving.provingGround}
              selection={selection}
              proving={proving}
              params={params}
              participants={participants}
            />
          )}
          {params.block === 'fix4' && (
            <FerryApprovalList
              items={
                proving && proving.participants ? proving.participants : []
              }
              provingGround={proving.provingGround}
              selection={selection}
              proving={proving}
              params={params}
              participants={participants}
            />
          )}
          {params.block === 'fix5' && (
            <RouteHotelApprovalList
              items={
                proving && proving.participants ? proving!.participants : []
              }
              provingGround={proving.provingGround}
              selection={selection}
              proving={proving}
              params={params}
              participants={participants}
            />
          )}
          {params.block === 'block1' && (
            <DocumentList items={block1Items() as any} selection={selection} />
          )}
          {params.block === 'block2' && (
            <DocumentList items={block2Items() as any} selection={selection} />
          )}
          {params.block === 'block3' && (
            <DocumentList items={block3Items() as any} selection={selection} />
          )}
          {params.block === 'block4' && (
            <DocumentList items={block4Items() as any} selection={selection} />
          )}
          {params.block === 'block5' && (
            <DocumentList items={block5Items() as any} selection={selection} />
          )}
          {params.block === 'block6' && (
            <DocumentList items={block6Items() as any} selection={selection} />
          )}
          {params.block === 'block7' && (
            <DocumentList items={block7Items() as any} selection={selection} />
          )}
          {params.block === 'block8' && (
            <DocumentList items={block8Items() as any} selection={selection} />
          )}
          {params.block === 'block9' && (
            <DocumentList items={block9Items() as any} selection={selection} />
          )}
          {params.block === 'block10' && (
            <DocumentList items={block10Items() as any} selection={selection} />
          )}
        </Section>
        <FormikPanel
          {...panelState}
          onSubmit={handleSubmit}
          validationSchema={new ProvingDetailsDocumentFormSchema()}
        >
          <ProvingDetailsDocumentForm
            participants={participants}
            provingGround={proving.provingGround}
            proving={proving}
          />
        </FormikPanel>
        <FormikPanel
          {...annotationPanelState}
          onSubmit={handleAnnotationSubmit}
        >
          {formType !== undefined && formType === FormType.annotation && (
            <ProvingDetailsAnnotationForm />
          )}
        </FormikPanel>
        <ConfirmDeleteDialog
          {...dialogState}
          itemCount={selectedItems.length}
          onConfirm={async () => {
            await deleteDocuments({
              variables: {
                provingId: proving._id,
                documentIds: selectedItems.map((x) => x._id),
              },
            })
            hideDialog()
          }}
        />
      </Page>
    </FlexSpinner>
  )
}
